import React from "react";
import { useAppSelector } from "../../../shared/store/hooks";
import { PeriodDashboard, isExcludedMember } from "./PeriodDashboard";
import { FONTS, NW } from "../../../shared/ui/tokens";

// ── AVDASH brand palette ──────────────────────────────────────────────────────
// ── Dashboard ─────────────────────────────────────────────────────────────────
const Dashboard: React.FC = () => {
  const { data, loading, error } = useAppSelector((s) => s.results);
  const rowDimCount = useAppSelector((s) => s.query.rows.length);

  const visibleRowsCount = React.useMemo(() => {
    if (!data) return 0;
    const rowCols = Math.min(rowDimCount, data.columns.length);
    if (rowCols === 0) return data.rows.length;
    return data.rows.filter((row) => {
      for (let i = 0; i < rowCols; i++) {
        if (isExcludedMember(row[i])) return false;
      }
      return true;
    }).length;
  }, [data, rowDimCount]);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 240,
          gap: 14,
          background: NW.white,
          borderRadius: 16,
          border: `1.5px solid ${NW.gray200}`,
          boxShadow: "0 2px 16px rgba(24,75,140,0.07)",
          fontFamily: FONTS.body,
        }}
      >
        <span
          style={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            border: `3px solid ${NW.violetMed}`,
            borderTopColor: NW.violet,
            display: "inline-block",
            animation: "nwSpin 0.7s linear infinite",
            flexShrink: 0,
          }}
        />
        <span style={{ fontSize: 14, color: NW.gray500, fontWeight: 600 }}>
          Exécution de la requête…
        </span>
        <style>{`@keyframes nwSpin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          color: NW.danger,
          fontSize: 13,
          padding: "12px 18px",
          background: NW.dangerLight,
          borderRadius: 12,
          border: `1px solid ${NW.danger}30`,
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontFamily: FONTS.body,
        }}
      >
        ⚠ {error}
      </div>
    );
  }

  if (!data) {
    return (
      <div
        style={{
          color: NW.gray500,
          padding: 60,
          textAlign: "center",
          fontSize: 14,
          background: NW.white,
          borderRadius: 16,
          border: `1.5px dashed ${NW.violetMed}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
          fontFamily: FONTS.body,
        }}
      >
        <span style={{ fontSize: 48 }}>📊</span>
        <div
          style={{
            fontWeight: 800,
            color: NW.violet,
            fontSize: 16,
            fontFamily: FONTS.heading,
          }}
        >
          Aucune donnée
        </div>
        <div style={{ fontSize: 13, color: NW.gray500, lineHeight: 1.7 }}>
          Exécutez d'abord une requête pour voir le dashboard.
        </div>
      </div>
    );
  }

  if (!data.rows.length) {
    return (
      <div
        style={{
          color: NW.gray500,
          padding: 60,
          textAlign: "center",
          fontSize: 14,
          background: NW.white,
          borderRadius: 16,
          border: `1.5px dashed ${NW.violetMed}`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 12,
          fontFamily: FONTS.body,
        }}
      >
        <span style={{ fontSize: 48 }}>🔍</span>
        <div
          style={{
            fontWeight: 800,
            color: NW.violet,
            fontSize: 16,
            fontFamily: FONTS.heading,
          }}
        >
          Requête sans résultat
        </div>
        <div style={{ fontSize: 13, color: NW.gray500 }}>
          La requête n'a retourné aucune ligne.
        </div>
      </div>
    );
  }

  return (
    <div style={{ fontFamily: FONTS.body }}>
      {/* ── Header ── */}
      <div
        style={{
          background: `linear-gradient(135deg, ${NW.violetDark} 0%, ${NW.violet} 55%, #1D5BA8 100%)`,
          borderRadius: 16,
          padding: "16px 22px",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          gap: 14,
          boxShadow: "0 6px 32px rgba(24,75,140,0.28)",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {/* Decorative glow */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${NW.rose}80, ${NW.rose}CC, ${NW.rose}80, transparent)`,
            pointerEvents: "none",
          }}
        />
        <div
          style={{
            width: 44,
            height: 44,
            borderRadius: 12,
            background: "rgba(255,255,255,0.12)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 22,
            flexShrink: 0,
            boxShadow: "0 0 0 1px rgba(255,255,255,0.15)",
          }}
        >
          📊
        </div>
        <div>
          <div
            style={{
              fontFamily: FONTS.heading,
              fontWeight: 800,
              fontSize: 18,
              color: "#FFFFFF",
              letterSpacing: -0.4,
            }}
          >
            Dashboard
          </div>
          <div
            style={{
              fontSize: 11,
              color: "rgba(255,255,255,0.5)",
              fontWeight: 600,
              letterSpacing: 2,
              textTransform: "uppercase",
              marginTop: 2,
            }}
          >
            Visualisation · Résultats
          </div>
        </div>
        {/* KPI pill */}
        <div
          style={{
            marginLeft: "auto",
            textAlign: "center",
            background: "rgba(255,255,255,0.10)",
            border: "1px solid rgba(255,255,255,0.15)",
            borderRadius: 12,
            padding: "8px 18px",
          }}
        >
          <div
            style={{
              fontSize: 20,
              fontWeight: 800,
              color: "#FFFFFF",
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {visibleRowsCount}
          </div>
          <div
            style={{
              fontSize: 10,
              color: "rgba(255,255,255,0.55)",
              textTransform: "uppercase",
              letterSpacing: 1.2,
            }}
          >
            Lignes
          </div>
        </div>
      </div>

      <PeriodDashboard data={data} rowDimCount={rowDimCount} />
    </div>
  );
};

export default Dashboard;
